import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FutureContractsQuery } from '~/QueryHooks/FutureContractsQuery'
import Layout from '~/components/Layout'
import i18n from '~/components/i18n'
import { DrawwerWrapper, StyledInfoPopUpIcon, StyledTab } from './style'
import TableComponent from '~/components/TableComponent'
import { Checkbox, Dropdown, Form, Icon } from 'semantic-ui-react'
import Dialog from '~/components/Dialog'
import notify from '~/components/Notify'
import FutureContractsService, { StoneXCountType } from '~/api/services/futureContracts'
import Drawer from '~/components/Drawer'
import ContractsComments from './ContractComments'
import { useAuth } from '~/routes/components/AuthProvider'
import { LIGHT_USER_ROLE_SLUG, stoneXFilters } from '~/Constants'
import Filters from './Filters'
import { StyledFiltersOptions } from '../../DairyFactory/Farmers/style'
import { ThemeContext } from 'styled-components'
import axios from 'axios'
import ENV from '~/environment';
import ContractInfo from './ContractInfo'
import { ModalHeaderWrapper } from '../style'
import ExcelExport from '~/components/ExcelExport'
import { handleErrors } from '~/utils/helpers'

const getDefaultTableColumnHeaders = () => [
  {
    name: i18n.t('Unique ID'),
    key: 'uniqueId',
    sorted: 'desc'
  },
  {
    name: i18n.t('Status'),
    key: 'statusLabel'
  },
  {
    name: i18n.t('Commodity'),
    key: 'commodityLabel',
    sorted: 'desc'
  },
  {
    name: i18n.t('Trading month'),
    key: 'tradingMonth',
    type: 'convertNumberToMonth',
    sorted: 'desc'
  },
  {
    name: i18n.t('Exchange'),
    key: 'exchangeName'
  },
  {
    name: i18n.t('Position'),
    key: 'type'
  },
  {
    name: i18n.t('Contract size (in tonnes)'),
    key: 'contractSize',
    sorted: 'desc'
  },
  {
    name: i18n.t('Contract No'),
    key: 'contractNo',
    sorted: 'desc'
  },
  {
    name: i18n.t('Contract price (in €)'),
    key: 'price',
    type: 'no-unit-number',
    sorted: 'desc'
  },
  {
    name: i18n.t('Cash Settlement (in €)'),
    type: 'no-unit-number-no-value',
    key: 'settlementPrice',
    sorted: 'desc'
  },
  {
    name: i18n.t('Trading slot'),
    key: 'tradingSlotUniqueId',
    type: 'tradingSlotLink'
  },
  {
    name: i18n.t('Actions')
  }
]

export type StoneXFiltersType = {
  page: number;
  rows: number;
  search: string;
  status: string;
  tradingMonth: string;
  tradingYear: string;
  exchangeId: string;
  commodityId: string;
  contractNoFrom: string;
  contractNoTo: string;
  priceFrom: string;
  priceTo: string;
  type: string;
  withTradingSlot: number;
};

const StoneX: React.FC = () => {
  const { t } = useTranslation()
  const { firstColor } = useContext(ThemeContext)
  const { systemData: { logged: { roleSlug } } } = useAuth()
  const lightUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG
  const [stoneXData, setStoneXData] = useState(null)
  const [countData, setCountData] = useState<StoneXCountType[]>([])
  const [isBusy, setIsBusy] = useState(false)
  const [isFetchingData, setIsFetchingData] = useState(false)
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [contractCommentData, setContractCommentData] = useState({
    isVisible: false,
    contractId: ''
  })
  const [loadingAction, setLoadingAction] = useState({
    type: '',
    id: null,
    value: false,
    idKey: ''
  })
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableColumnHeaders())
  const [contractInfo, setContractInfo] = useState(null)
  const [blockRowLoading, setBlockRowLoading] = useState(false)
  const [cancelToken, setCancelToken] = useState(null)
  const [checkedFilters, setCheckedFilters] = useState([])
  const [tradingSlotList, setTradingSlotList] = useState([])
  const [modalData, setModalData] = useState({
    open: false,
    id: null,
    contractId: ''
  })
  const [dataFiltered, setDataFiltered] = useState<StoneXFiltersType>({
    page: 0,
    rows: 20,
    search: '',
    status: '',
    tradingMonth: '',
    tradingYear: '',
    exchangeId: '',
    commodityId: '',
    contractNoFrom: '',
    contractNoTo: '',
    priceFrom: '',
    priceTo: '',
    type: '',
    withTradingSlot: 0
  })

  const actions = [
    {
      popupMessage: i18n.t('Show'),
      type: 'icon',
      name: 'eye',
      size: 'large',
      actionType: 'show'
    },
    {
      popupMessage: i18n.t('Edit'),
      type: 'icon',
      name: 'pencil',
      size: 'large',
      actionType: 'edit'
    },
    {
      popupMessage: i18n.t('Comments'),
      type: 'icon',
      name: 'comments',
      size: 'large',
      actionType: 'comments',
      lightUserRole: true
    },
    {
      popupMessage: i18n.t('Latest comment'),
      type: 'icon',
      name: 'pencil',
      size: 'large',
      actionType: 'lattestComment',
      renderActions: getActions
    }
  ]

  const {
    isLoading: isBusyOptions,
    data: contractsOptions,
    refetch: refetchOptions
  } = FutureContractsQuery.getContractsData()

  useEffect(() => {
    const filters = localStorage.getItem('stoneXFilters')
    if (filters) {
      setCheckedFilters(JSON.parse(filters))
    }
  }, [])

  useEffect(() => {
    if (cancelToken) {
      cancelToken.cancel('');
    }
    fetchStoneXData()
  }, [dataFiltered]);

  useEffect(() => {
    setTableColumnHeaders(getDefaultTableColumnHeaders())
    refetchOptions()
  }, [t])

  useEffect(() => {
    if (modalData.open && tradingSlotList.length === 0) {
      fetchTradingSlotList()
    }
  }, [modalData.open])

  function getActions(row, type) {
    return row?.latestComment && type === 'lattestComment' && <StyledInfoPopUpIcon
      color={firstColor}
      content={row?.latestComment}
      position={'top left'}
      trigger={<Icon style={{ fontSize: '1.4em', color: firstColor }} name='info circle' />}
    />
  }

  async function fetchStoneXData(withCount = true) {
    const source = axios.CancelToken.source();
    setCancelToken(source);
    setBlockRowLoading(!withCount)
    setIsFetchingData(withCount)
    const payload = { ...dataFiltered, page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1 }
    try {
      if (withCount) {
        await axios.post(`${ENV.API_URL}/contracts/index`, payload, {
          headers: { 'Authorization': localStorage.getItem('token') },
          cancelToken: source.token
        }).then((response) => {
          setStoneXData(response.data)
        })
        const response = await FutureContractsService.getStoneXContractsCount(payload)
        setCountData(response.data)
      } else {
        const stoneResponse = await FutureContractsService.getStoneXContracts(payload)
        setStoneXData(stoneResponse.data)
      }
    } catch (error) {
      handleErrors(error)
    }
    setBlockRowLoading(false)
    setIsFetchingData(false)
  }

  async function fetchTradingSlotList() {
    setIsBusy(true);
    try {
      const response = await FutureContractsService.getContractsOptions()
      setTradingSlotList(response.data.map(tradingSlot => ({ value: tradingSlot?.id, text: tradingSlot?.unique_id })))
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function handleTabChange(activeIndex) {
    if (!isFetchingData) {
      const status = contractsOptions?.statuses.find((_, indexOption) => indexOption === activeIndex)
      setActiveTabIndex(activeIndex)
      setDataFiltered({
        ...dataFiltered,
        status: status.key.toString(),
        page: 0
      })
    }
  }

  async function handleloadContractInfo(contractId) {
    setLoadingAction({
      type: 'show',
      id: contractId,
      value: true,
      idKey: 'id'
    })
    try {
      const response = await FutureContractsService.getContractData(contractId)
      setContractInfo(response.data)
    } catch (error) {
      handleErrors(error)
    }
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  function handleActionClick(row, type) {
    switch (type) {
      case 'show':
        return handleloadContractInfo(row?.id)
      case 'edit':
        return setModalData({ id: row?.tradingSlotId, open: true, contractId: row?.id })
      case 'comments':
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
        return setContractCommentData({ contractId: row?.id, isVisible: true })
      default:
        return
    }
  }

  function renderContent() {
    const { contractId } = contractCommentData
    const actionsFiltered = lightUserLogged ? actions.filter(action => !!action.lightUserRole) : actions
    return <TableComponent
      actions={actionsFiltered}
      disableRowId={blockRowLoading ? contractId || modalData?.contractId : ''}
      tableColumnHeaders={tableColumnHeaders}
      animated={blockRowLoading ? !(contractId || modalData?.contractId) : true}
      isFetchingData={isFetchingData}
      loadingAction={loadingAction}
      handleClickAction={(row, type) => handleActionClick(row, type)}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setTableColumnHeaders(tableColumnHeaders)
        setDataFiltered(data)
      }}
      rows={rows}
      total={filtered}
    />
  }

  function renderPanes() {
    const panes = contractsOptions?.statuses.map(option => {
      const count = countData.find(count => count.status === parseInt(option.key))
      return {
        menuItem: `${option.label} (${count?.count || 0})`,
        render: () => renderContent()
      }
    })

    return <StyledTab
      menu={{ secondary: true, pointing: true }}
      activeIndex={activeTabIndex}
      panes={panes}
      onTabChange={(_, data) => handleTabChange(data.activeIndex)}
    />
  }

  function renderFilters() {
    return <Filters
      dataFiltered={dataFiltered}
      filterOptions={contractsOptions}
      checkedFilters={checkedFilters}
      onChangeDataFiltered={(dataFiltered) => setDataFiltered(dataFiltered)}
    />
  }

  async function handleAddTradingSlotToContractor() {
    try {
      await FutureContractsService.updateContract(modalData.contractId, { tradingSlotId: modalData.id })
      notify(t('Successfully added trading slot to contractor'), 'success')
      setModalData({ ...modalData, id: null, open: false })
      await fetchStoneXData(false)
      setModalData({ open: false, id: null, contractId: '' })
    } catch (error) {
      handleErrors(error)
    }
  }

  function renderEditModal() {
    return <Dialog
      isDialogOpen={modalData.open}
      textHeader={t('Add trading slot to contractor')}
      textClose={t('Close')}
      textSubmit={t('Add')}
      isLoading={isBusy}
      disableSubmit={!modalData.open}
      htmlContent
      handleClose={() => setModalData({ open: false, id: null, contractId: '' })}
      textContent={<Form>
        <Form.Dropdown
          required
          selectOnBlur={false}
          label={t('Trading slots')}
          placeholder={t('Select trading slot')}
          selection
          search
          options={tradingSlotList}
          value={modalData.id}
          onChange={(e, data) => setModalData({ ...modalData, id: data.value })}
        />
      </Form>}
      handleSubmit={() => handleAddTradingSlotToContractor()}
      type={'abort'}
    />
  }

  function renderDrawer() {
    const { contractId, isVisible } = contractCommentData
    return <Drawer
      content={<ContractsComments
        contractId={contractId}
      />}
      header={t('Comments')}
      onHide={async () => {
        setContractCommentData({ ...contractCommentData, isVisible: false })
        await fetchStoneXData(false)
        setContractCommentData(prevData => prevData.contractId !== contractCommentData.contractId ? prevData : { contractId: '', isVisible: false })
      }}
      isBusy={isBusy}
      isVisible={isVisible}
      closeOnEscape
    />
  }

  function renderActions() {
    return <>
      <ExcelExport
        url={'contracts/export'}
        fileName={t('contracts')}
      />
      <StyledFiltersOptions
        text={t('Filters')}
        icon='filter'
        floating
        labeled
        button
        closeOnChange={false}
        multiple
        className='icon'
        style={{ background: firstColor, color: 'white' }}
      >
        <Dropdown.Menu>
          <Dropdown.Menu scrolling>
            {stoneXFilters.map(filter => {
              return <Dropdown.Item
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                }}
                key={filter.key}
              >
                <Checkbox
                  onClick={(e, data) => {
                    e.stopPropagation()
                    e.preventDefault()
                    if (data.checked) {
                      setCheckedFilters([...checkedFilters, filter])
                      localStorage.setItem('stoneXFilters', JSON.stringify([...checkedFilters, filter]))
                    } else {
                      setCheckedFilters(checkedFilters.filter(filterChecked => filterChecked.key !== filter.key))
                      localStorage.setItem('stoneXFilters', JSON.stringify(checkedFilters.filter(filterChecked => filterChecked.key !== filter.key)))
                    }
                  }}
                  checked={Boolean(checkedFilters.find(filterChecked => filterChecked.key === filter.key))}
                  //@ts-ignore
                  label={t(filter.placeholder || filter.label)}
                />
              </Dropdown.Item>
            })}
          </Dropdown.Menu>
        </Dropdown.Menu>
      </StyledFiltersOptions>
    </>
  }

  function renderShowContractModal() {
    return <Dialog
      isDialogOpen={Boolean(contractInfo)}
      textHeader={<ModalHeaderWrapper>
        <Icon name='chart line' circular />
        <div>{t('Contract info')}</div>
      </ModalHeaderWrapper>}
      textContent={<ContractInfo data={contractInfo} />}
      handleClose={() => setContractInfo(null)}
    />
  }

  const { filtered, rows } = stoneXData ? stoneXData : { filtered: 0, rows: [] };

  const loading = isBusyOptions || !stoneXData || isFetchingData

  return <Layout
    title={t('StoneX')}
    segmented
    filters={contractsOptions && renderFilters()}
    actions={renderActions()}
    isBusy={loading}
  >
    {contractInfo && renderShowContractModal()}
    <DrawwerWrapper>
      {renderDrawer()}
    </DrawwerWrapper>
    {renderEditModal()}
    {countData && stoneXData && contractsOptions && renderPanes()}
  </Layout>
}
export default StoneX